import { getMetadataForRoute } from "@/helpers/metadata";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Hook to apply metadata for current route
 * Automatically extracts and applies metadata from all layouts in the route hierarchy
 */
export function useMetadata() {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const applyMetadata = async () => {
      try {
        // Get metadata for current route
        const metadata = await getMetadataForRoute(location.pathname);

        if (!isMounted) return;

        if (metadata) {
          // Set document title
          if (metadata.title) {
            document.title = metadata.title;
          }

          // Set meta description
          if (metadata.description) {
            let metaDescription = document.querySelector('meta[name="description"]');
            if (!metaDescription) {
              metaDescription = document.createElement("meta");
              metaDescription.setAttribute("name", "description");
              document.head.appendChild(metaDescription);
            }
            metaDescription.setAttribute("content", metadata.description);
          }

          // Set Open Graph tags if provided
          if (metadata.openGraph) {
            if (metadata.openGraph.title) {
              let ogTitle = document.querySelector('meta[property="og:title"]');
              if (!ogTitle) {
                ogTitle = document.createElement("meta");
                ogTitle.setAttribute("property", "og:title");
                document.head.appendChild(ogTitle);
              }
              ogTitle.setAttribute("content", metadata.openGraph.title);
            }

            if (metadata.openGraph.description) {
              let ogDescription = document.querySelector('meta[property="og:description"]');
              if (!ogDescription) {
                ogDescription = document.createElement("meta");
                ogDescription.setAttribute("property", "og:description");
                document.head.appendChild(ogDescription);
              }
              ogDescription.setAttribute("content", metadata.openGraph.description);
            }

            // Set Open Graph images if provided
            if (metadata.openGraph.images && metadata.openGraph.images.length > 0) {
              const firstImage = metadata.openGraph.images[0];
              let ogImage = document.querySelector('meta[property="og:image"]');
              if (!ogImage) {
                ogImage = document.createElement("meta");
                ogImage.setAttribute("property", "og:image");
                document.head.appendChild(ogImage);
              }
              ogImage.setAttribute("content", firstImage.url);
            }
          }

          // Set Twitter Card tags if provided
          if (metadata.twitter) {
            if (metadata.twitter.card) {
              let twitterCard = document.querySelector('meta[name="twitter:card"]');
              if (!twitterCard) {
                twitterCard = document.createElement("meta");
                twitterCard.setAttribute("name", "twitter:card");
                document.head.appendChild(twitterCard);
              }
              twitterCard.setAttribute("content", metadata.twitter.card);
            }

            if (metadata.twitter.title) {
              let twitterTitle = document.querySelector('meta[name="twitter:title"]');
              if (!twitterTitle) {
                twitterTitle = document.createElement("meta");
                twitterTitle.setAttribute("name", "twitter:title");
                document.head.appendChild(twitterTitle);
              }
              twitterTitle.setAttribute("content", metadata.twitter.title);
            }

            if (metadata.twitter.description) {
              let twitterDescription = document.querySelector('meta[name="twitter:description"]');
              if (!twitterDescription) {
                twitterDescription = document.createElement("meta");
                twitterDescription.setAttribute("name", "twitter:description");
                document.head.appendChild(twitterDescription);
              }
              twitterDescription.setAttribute("content", metadata.twitter.description);
            }
          }
        }
      } catch (error) {
        console.error("Failed to apply metadata:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    applyMetadata();

    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  return { isLoading };
}
